// Data
export type PageStatus = "idle" | "loading" | "success" | "error";
export type Result<T> =
    | { ok: true;  data: T }
    | { ok: false; error: string; status?: number };

// Opisy ocen w gwiazdkach - używane zarówno przy wystawianiu opinii (etykieta pod gwiazdkami
// po najechaniu), jak i w rozkładzie ocen (5*/4*/... jak w Google Maps) na profilu wykładowcy.
export const STAR_RATING_LABELS: { [key: number]: string } = {
    5: "Wybitny",
    4: "Świetny",
    3: "W porządku",
    2: "Słaby",
    1: "Beznadziejny",
};

// Osobny zestaw etykiet dla oceny EGZAMINU (ten sam widget gwiazdkowy co Comment.rating, ale
// "Wybitny"/"Beznadziejny" nie pasuje do egzaminu - tu chodzi o to, jak przyjemnie/nieprzyjemnie
// przebiegł, nie o jego jakość merytoryczną).
export const EXAM_RATING_LABELS: { [key: number]: string } = {
    5: "Bardzo przyjemny",
    4: "Przyjemny",
    3: "Neutralny",
    2: "Nieprzyjemny",
    1: "Straszny",
};

// Rozkład ocen (ile 5*/4*.../"5-Piekielny"/"4-Trudny" itd.) - jak w Google Maps, poziome paski.
// Wspólna implementacja dla renderRatingDistribution (posts.ts, gwiazdki) i
// renderDifficultyDistribution (subjects.ts, trudność/🔥) - poprzednio dwie osobne,
// niemal identyczne kopie tej samej logiki, które realnie rozjechały się wizualnie (zgłoszenie:
// "rozkład ogienków ma inną szerokość niż gwiazdek") - jedna implementacja, różniąca się tylko
// słownikiem etykiet, eliminuje takie rozjazdy z definicji zamiast wymagać ręcznej synchronizacji
// dwóch kopii przy każdej zmianie.
export function renderLevelDistribution(
    distribution: { [key: string]: number },
    labels: { [key: number]: string },
    options?: { showLabels?: boolean }
): HTMLElement {
    const showLabels = options?.showLabels ?? true;

    const container = document.createElement("div");
    container.className = "rating-distribution";

    const total = [1, 2, 3, 4, 5].reduce((sum, level) => sum + (distribution[String(level)] ?? 0), 0);

    for (let level = 5; level >= 1; level--) {
        const count = distribution[String(level)] ?? 0;
        const percent = total > 0 ? (count / total) * 100 : 0;

        const row = document.createElement("div");
        row.className = "rating-distribution-row";

        const label = document.createElement("span");
        label.className = "rating-distribution-label";
        label.textContent = showLabels ? `${level} - ${labels[level]}` : String(level);

        const barTrack = document.createElement("span");
        barTrack.className = "rating-distribution-bar-track";
        const barFill = document.createElement("span");
        barFill.className = "rating-distribution-bar-fill";
        barFill.style.width = `${percent}%`;
        barTrack.appendChild(barFill);

        const countEl = document.createElement("span");
        countEl.className = "rating-distribution-count";
        countEl.textContent = String(count);

        row.append(label, barTrack, countEl);
        container.appendChild(row);
    }

    return container;
}

// ── Wyłączniki funkcji (na życzenie, tymczasowo) ─────────────────────────────────────────
// Pełny, aktualny spis tego, co jest wyłączone i dlaczego: patrz WYLACZONE_FUNKCJE.md w
// katalogu głównym repo - aktualizuj go razem z każdą zmianą tutaj.

// "Wybrałbyś/wybrałabyś ponownie?" - jeden wyłącznik dla formularza (renderWouldTakeAgainField
// w comments.ts) i odznaki z procentem (renderWouldTakeAgainBadge w posts.ts).
export const WOULD_TAKE_AGAIN_ENABLED = true;

// Przycisk "+ Dodaj komentarz do tego przedmiotu" pod nagłówkiem sekcji przedmiotu w widoku
// zgrupowanym (patrz renderGroupedView w post_detail.ts) - dodawanie opinii do konkretnego
// przedmiotu wciąż jest możliwe przez główny formularz na górze strony (wyszukiwarka
// przedmiotu), znika tylko ten skrót. "Dodaj komentarz ogólny" (ten sam przycisk, inny
// label, dla sekcji "Komentarz ogólny") zostaje włączony - nie był objęty prośbą o wyłączenie.
export const SUBJECT_QUICK_ADD_ENABLED = false;

// Formularz dodawania opinii (patrz initCommentForm w comments.ts) w OSOBNYM oknie (natywny
// <dialog>, budowany w JS - patrz post_detail.ts) zamiast rozwijanej sekcji wprost na stronie
// profilu - więcej miejsca na etykiety/podpowiedzi (gwiazdki/przedmiot ginęły w ciasnym wierszu
// inline). Wyłączenie cofa 1:1 do starego zachowania BEZ zmian w szablonie HTML - dialog jest
// budowany przez przeniesienie #comment-form w JS tylko gdy ta flaga jest włączona.
export const ADD_REVIEW_MODAL_ENABLED = true;

// Edycja PRZYPISANIA przedmiotów do roku (dodaj/usuń przedmiot z "N rok" - patrz
// buildYearSection w subjectYearDirectory.ts) na wyraźną prośbę (2026-09-05) tymczasowo
// schowana - edycja PROWADZĄCYCH danego przedmiotu (buildTeachersEditor, ten sam plik) zostaje
// WŁĄCZONA, to osobna flaga/funkcja, nietknięta.
export const SUBJECT_YEAR_EDITING_ENABLED = false;


// Functions

// Zapamiętuje (w tej przeglądarce), do których profili user już dodał opinię - żeby przy
// ponownym kliknięciu "Dodaj opinię" na TYM SAMYM profilu od razu podpowiedzieć limit (patrz
// post_detail.ts), zamiast żeby user wypełnił cały formularz i dopiero przy
// wysyłce dostał błąd 429 z backendu ("Możesz dodać tylko jedną opinię do tego profilu.", patrz
// pages/api/comments.py). To tylko podpowiedź z wyprzedzeniem - backend i tak jest ostatecznym
// egzekwującym limit (żeby "wyczyszczenie" localStorage nie omijało go). Wspólne dla wszystkich
// miejsc, z których da się dodać opinię (profil i popup "Dodaj osobę" - patrz addLecturer.ts),
// żeby wszystkie zgadzały się co do tego, co user już opiniował.
const REVIEWED_POSTS_STORAGE_KEY = "mumin_reviewed_post_ids";

export function hasAlreadyReviewedThisPost(postId: number): boolean {
    try {
        const saved = localStorage.getItem(REVIEWED_POSTS_STORAGE_KEY);
        const ids: unknown = saved ? JSON.parse(saved) : [];
        return Array.isArray(ids) && ids.includes(postId);
    } catch {
        return false;
    }
}

export function markPostAsReviewed(postId: number): void {
    try {
        const saved = localStorage.getItem(REVIEWED_POSTS_STORAGE_KEY);
        const parsed: unknown = saved ? JSON.parse(saved) : [];
        const ids: number[] = Array.isArray(parsed) ? parsed : [];
        if (!ids.includes(postId)) {
            localStorage.setItem(REVIEWED_POSTS_STORAGE_KEY, JSON.stringify([...ids, postId]));
        }
    } catch { /* ignore */ }
}

// Zapamiętuje ostatnio wpisany e-mail powiadomień (w tej przeglądarce), żeby nie trzeba go było
// wpisywać od nowa za każdym razem, gdy widżet się pojawi (patrz renderNotifyEmailField niżej).
// Wygasa po 12 miesiącach od ostatniego wpisania - ten sam limit i uzasadnienie co retencja
// adresów e-mail w bazie (rules.html §5.5, patrz RETENTION_DAYS w purge_old_ip_addresses.py) -
// localStorage nie egzekwuje wygasania samo, więc sprawdzamy datę zapisu przy każdym odczycie.
const NOTIFY_EMAIL_STORAGE_KEY = "mumin_notify_email";
const NOTIFY_EMAIL_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

function loadSavedNotifyEmail(): string {
    try {
        const raw = localStorage.getItem(NOTIFY_EMAIL_STORAGE_KEY);
        if (!raw) return "";
        const parsed: unknown = JSON.parse(raw);
        if (
            typeof parsed !== "object" || parsed === null
            || typeof (parsed as { email?: unknown }).email !== "string"
            || typeof (parsed as { savedAt?: unknown }).savedAt !== "number"
        ) {
            return "";
        }
        const { email, savedAt } = parsed as { email: string, savedAt: number };
        if (Date.now() - savedAt > NOTIFY_EMAIL_MAX_AGE_MS) {
            localStorage.removeItem(NOTIFY_EMAIL_STORAGE_KEY);
            return "";
        }
        return email;
    } catch {
        return "";
    }
}

function saveNotifyEmail(email: string): void {
    try {
        if (email) {
            localStorage.setItem(NOTIFY_EMAIL_STORAGE_KEY, JSON.stringify({ email, savedAt: Date.now() }));
        } else {
            localStorage.removeItem(NOTIFY_EMAIL_STORAGE_KEY);
        }
    } catch { /* ignore */ }
}

// Opcjonalne pole e-maila do powiadomienia o decyzji moderacyjnej (zatwierdzono/odrzucono) -
// ten sam widżet w formularzu opinii, dodania wykładowcy, wydziału i uczelni (patrz comments.ts/
// addLecturer.ts/landing2.ts) - stąd wspólny helper zamiast kopiowania w czterech miejscach.
// Sama wartość (notify_email) jest opcjonalna po stronie backendu (EmailField(blank=True)) -
// puste pole to normalny, oczekiwany przypadek, nie błąd walidacji.
export function renderNotifyEmailField(): { element: HTMLElement, getValue: () => string, reset: () => void } {
    const wrapper = document.createElement("label");
    wrapper.className = "notify-email-field";

    const labelText = document.createElement("span");
    labelText.className = "field-label-text";
    labelText.textContent = "Twój e-mail (opcjonalnie - dowiesz się o decyzji):";

    const input = document.createElement("input");
    input.type = "email";
    input.placeholder = "np. jan.kowalski@example.com";
    input.autocomplete = "email";
    // Podpowiedź z tego, co user wpisał ostatnim razem (patrz loadSavedNotifyEmail wyżej) - i tak
    // trzeba by to wpisać ponownie w kolejnym miejscu, gdzie ten widżet się pojawi. Ustawiamy
    // zarówno .value, jak i .defaultValue (nie tylko atrybut "value" - .defaultValue to właśnie
    // ten atrybut pod spodem) - ten widżet ląduje wewnątrz <form> (patrz notifyPromptContainer w
    // comments.ts/addLecturer.ts), które po udanym zapisie woła form.reset(); bez .defaultValue
    // reset cofałby pole z powrotem do pustego (bo .value samo w sobie nie zmienia "wartości
    // domyślnej" pola, do której reset() wraca).
    const savedEmail = loadSavedNotifyEmail();
    input.value = savedEmail;
    input.defaultValue = savedEmail;
    input.addEventListener("input", () => saveNotifyEmail(input.value.trim()));

    wrapper.append(labelText, input);

    return {
        element: wrapper,
        getValue: () => input.value.trim(),
        reset: () => { input.value = ""; },
    };
}

// Ostatnio wybrany wydział (uczelnia+wydział) w tej przeglądarce - współdzielone między
// landing2.ts (pełny picker /inne/) i landing3.ts (kafelki na stronie głównej) - wybór z
// jednego miejsca ma też pozwolić wrócić z powrotem tym samym zapamiętanym wyborem w drugim,
// stąd jeden wspólny zestaw kluczy zamiast dwóch niezależnych.
// UWAGA: te same dwa stringi są zduplikowane WPROST (nie przez import - to inline <script> w
// Django template, nie plik .ts) w blokującym skrypcie anty-błysk w extra_head bloku
// landing3.html - zmiana tych kluczy TUTAJ wymaga ręcznej zmiany też TAM.
export const SELECTED_UNIVERSITY_STORAGE_KEY = "mumin_test2_selected_university";
export const SELECTED_FACULTY_STORAGE_KEY = "mumin_test2_selected_faculty";

export function getSavedValue(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

export function saveValue(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch { /* ignore */ }
}

export function clearValue(key: string): void {
    try {
        localStorage.removeItem(key);
    } catch { /* ignore */ }
}

export function getElement<T extends HTMLElement>(id: string): T {
    const el = document.getElementById(id);
    if (el === null) {
        throw new Error(`Element #${id} not found`);
    }
    return el as T;
}

// Normalizacja do wyszukiwania: bez polskich znaków diakrytycznych (ą,ć,ę,ł,ń,ó,ś,ź,ż), bez
// wielkości liter, bez nadmiarowych spacji - żeby "Galazka" znajdowało "Gałązka", a wielokrotne
// spacje między słowami nie psuły dopasowania.
const COMBINING_DIACRITICS_RE = /[\u0300-\u036f]/g;

export function normalizeForSearch(text: string): string {
    return text
        .normalize("NFD")
        .replace(COMBINING_DIACRITICS_RE, "") // usuwa znaki diakrytyczne rozkładalne przez NFD (ą,ć,ę,ń,ó,ś,ź,ż)
        .replace(/ł/gi, "l")                  // "ł" nie rozkłada się przez NFD - trzeba osobno
        .toLowerCase()
        .trim()
        .replace(/\s+/g, " ");
}

// Tolerancja literówek przy wyszukiwaniu (na wyraźną prośbę 2026-10-22, na razie wyszukiwarka
// uczelni - patrz universityMatch w universityPicker.ts). Te same progi i ten sam algorytm co
// pages/university_search.py po stronie serwera: jak "fuzziness: AUTO" w Elasticsearch, ale
// literówki dopiero od 5 znaków - przy 3-4 literach jedna zmiana trafia już w inne słowo.
export function allowedTypos(length: number): number {
    if (length >= 9) return 2;
    if (length >= 5) return 1;
    return 0;
}

// Odległość Damerau-Levenshteina (wariant OSA - zamiana sąsiednich liter = 1 błąd), przerywana,
// gdy cały wiersz przekroczy limit.
export function editDistance(a: string, b: string, limit: number): number {
    if (Math.abs(a.length - b.length) > limit) return limit + 1;
    let previous2: number[] | null = null;
    let previous = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i++) {
        const current = [i, ...new Array<number>(b.length).fill(0)];
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost);
            if (previous2 && i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                current[j] = Math.min(current[j], previous2[j - 2] + 1);
            }
        }
        if (Math.min(...current) > limit) return limit + 1;
        previous2 = previous;
        previous = current;
    }
    return previous[b.length];
}

// 0 - słowo zaczyna się od wpisanego; >0 - tyle literówek; null - nie pasuje. Z początkiem słowa
// porównujemy tylko, gdy słowo jest wyraźnie dłuższe (ktoś jeszcze pisze) - przy podobnej
// długości liczy się całe słowo, inaczej "warszawki" pasowałoby do "warszawi(e)" równie dobrze
// co do "warszawski".
export function tokenDistance(queryToken: string, word: string): number | null {
    if (word.startsWith(queryToken)) return 0;
    const limit = allowedTypos(queryToken.length);
    if (!limit) return null;
    const lengths = new Set([word.length]);
    if (word.length > queryToken.length + 1) {
        [queryToken.length - 1, queryToken.length, queryToken.length + 1].forEach(l => lengths.add(l));
    }
    let best: number | null = null;
    lengths.forEach(length => {
        if (length <= 0 || length > word.length) return;
        const distance = editDistance(queryToken, word.slice(0, length), limit);
        if (distance <= limit && (best === null || distance < best)) best = distance;
    });
    return best;
}

// Rozbija wpisaną frazę na imię/nazwisko do wstępnego uzupełnienia formularza dodania
// wykładowcy - pierwsze słowo to imię, reszta to nazwisko (jak przy jednym polu "Imię Nazwisko").
// Współdzielone między post_detail.ts (wyszukiwarka na profilu) i addLecturer.ts (wyszukiwarka
// na liście wykładowców uczelni/wydziału) - w obu miejscach otwarcie formularza dodania
// przepisuje to, co ktoś już wpisał w polu wyszukiwania, zamiast każąc wpisywać to od nowa.
export function splitFullName(query: string): { name: string, surname: string } {
    const parts = query.trim().split(/\s+/);
    return { name: parts[0] ?? "", surname: parts.slice(1).join(" ") };
}

export function debounce<T extends (...args: any[]) => void>(
    fn: T,
    delay: number
): (...args: Parameters<T>) => void {
    let timer: ReturnType<typeof setTimeout>;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
}

export async function withRetry<T>(
  fn: () => Promise<Result<T>>,
  retries: number,
  delayMs: number,

  onStatusUpdate?: (message: string) => void, 
  totalAttempts: number = retries + 1
): Promise<Result<T>> {
  const currentAttempt = totalAttempts - retries;
  
  const status = `Attempt ${currentAttempt}/${totalAttempts}…`;
  if (onStatusUpdate) onStatusUpdate(status); 
  console.log(status);

  const result = await fn();

  if (result.ok) return result;

  if (retries > 0) {
    const retryStatus = `Retry ${currentAttempt + 1}/${totalAttempts} (waiting ${delayMs}ms)…`;
    if (onStatusUpdate) onStatusUpdate(retryStatus);
    
    await sleep(delayMs);
    return withRetry(fn, retries - 1, delayMs * 2, onStatusUpdate, totalAttempts);
  }

  return result;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Dymek z wyjaśnieniem znaczenia ikony/odznaki (🎓 łatwość zaliczenia, 👍 poleca) - `title`
// (natywny tooltip) działa na hover na desktopie, ale na telefonie pokazuje się dopiero po
// długim przytrzymaniu (jeśli w ogóle) - za mało odkrywalne (na wyraźną prośbę: "żeby się
// pokazywało znaczenie... np po dotknięciu"). Klik/tap dokłada więc WŁASNY, widoczny dymek na
// chwilę - ten sam wzorzec "pokaż etykietę, potem sama zniknie" co showLabelBriefly w
// renderDifficultyRating (subjects.ts), tylko dla ODZNAK tylko-do-odczytu, nie interaktywnych
// pickerów. `title` zostaje jako fallback (czytniki ekranu, hover na desktopie pokazuje od razu,
// zanim ktoś w ogóle kliknie).
const TAP_TOOLTIP_HIDE_MS = 2500;

export function attachTapTooltip(el: HTMLElement, text: string): void {
    el.title = text;
    el.classList.add("tap-tooltip-target");

    let bubble: HTMLElement | null = null;
    let hideTimeout: ReturnType<typeof setTimeout> | undefined;

    el.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!bubble) {
            bubble = document.createElement("span");
            bubble.className = "tap-tooltip-bubble";
            bubble.textContent = text;
            el.appendChild(bubble);
        }
        bubble.classList.add("visible");
        if (hideTimeout !== undefined) clearTimeout(hideTimeout);
        hideTimeout = setTimeout(() => { bubble?.classList.remove("visible"); }, TAP_TOOLTIP_HIDE_MS);
    });
}


// Łatwość zaliczenia zajęć/egzaminu (Comment.class_difficulty/exam_difficulty - nazwa pola w
// bazie zostaje bez zmian, zmienia się tylko kierunek/etykieta: 5 = bardzo łatwo zaliczyć, tak
// jak gwiazdki - "dużo" znaczy "dobrze", NIE odwrotnie jak przy "trudności" - na wyraźną prośbę,
// żeby kierunek tej skali czytało się tak samo intuicyjnie jak ocenę). CELOWO zwykły tekst, nie
// gwiazdki (showStars wyżej) ani płomyki (showDifficulty w subjects.ts, tam Subject.difficulty -
// INNY model/kontekst/kierunek skali, nietknięty) - druga skala ikon obok już istniejących
// gwiazdek/rozkładu ocen byłaby nieczytelna, RMP-style pokazuje samą liczbę + "/5" (na wyraźną
// prośbę - odróżnia to od "zwykłej" liczby bez jednostki). Jedna funkcja dla pojedynczej opinii
// (liczba całkowita, np. "Łatwość zaliczenia: 3/5") i dla średniej (jedno miejsce po przecinku,
// np. "Łatwość zaliczenia: 2.7/5") - Number.isInteger rozróżnia oba przypadki bez osobnego parametru.
// 🎓 - ta sama ikona co w widżecie wyboru (renderDifficultyRating wołane z EASE_OF_PASSING_ICON
// w comments.ts) - na wyraźną prośbę wprowadzona też przy WYŚWIETLANIU, nie tylko przy wpisywaniu
// opinii, żeby to samo pojęcie zawsze rozpoznawać po tej samej ikonie.
const EASE_OF_PASSING_ICON = "\u{1F393}"; // 🎓

// Schowane CAŁOŚCIOWO na wyraźną prośbę (2026-09-04) - ten sam wzorzec co WOULD_TAKE_AGAIN_ENABLED
// niżej: jeden przełącznik, reszta mechanizmu (input w formularzu - patrz initCommentForm w
// comments.ts, dane w bazie/API) zostaje nietknięta, tylko renderowanie (tu i we wszystkich
// wołających - posts.ts/comments.ts/post_detail.ts/compareView.ts/subjectTeacherLinks.ts, patrz
// null-guardy przy każdym appendChild) cichnie, dopóki flaga jest false. Przywrócenie = jedna
// zmiana (true) tutaj, bez szukania po całym kodzie.
export const EASE_OF_PASSING_ENABLED = false;

export function renderEaseOfPassingText(value: number): HTMLElement | null {
    if (!EASE_OF_PASSING_ENABLED) return null;
    const el = document.createElement("span");
    el.className = "difficulty-text-badge";
    el.textContent = `${EASE_OF_PASSING_ICON} Łatwość zaliczenia: ${Number.isInteger(value) ? value : value.toFixed(1)}/5`;
    attachTapTooltip(el, "Jak łatwo zaliczyć/zdać u tego wykładowcy - 5 oznacza bardzo łatwo.");
    return el;
}

// Odpowiednik renderEaseOfPassingText wyżej, ale bez etykiety "Łatwość zaliczenia:" - na wyraźną
// prośbę, w miejscach gdzie pełny tekst zajmuje za dużo miejsca (lista prowadzących danego
// przedmiotu, patrz buildTeachersEditor w subjectTeacherLinks.ts). Ikona + sama liczba zostają
// widoczne (na wyraźną prośbę - "obok ikonki podaj tę łatwość"), pełne wyjaśnienie, co ta liczba
// znaczy, trafia do dymka (hover/tap - patrz attachTapTooltip).
export function renderEaseOfPassingIcon(value: number): HTMLElement | null {
    if (!EASE_OF_PASSING_ENABLED) return null;
    const el = document.createElement("span");
    el.className = "difficulty-text-badge difficulty-icon-only";
    el.textContent = `${EASE_OF_PASSING_ICON} ${Number.isInteger(value) ? value : value.toFixed(1)}/5`;
    attachTapTooltip(
        el,
        "Łatwość zaliczenia - 5 oznacza bardzo łatwo.",
    );
    return el;
}

export function showStars(rating : number): HTMLElement {
    const container = document.createElement("div");
    container.className = "star-rating";
    const stars : HTMLElement[] = [];

    for (let i = 1; i <= 5; i++) {
        const star = document.createElement("span");
        star.className = "star";
        star.innerHTML = "&#9733;"; 
        star.dataset.value = String(i);

        if (i <= rating) {
            star.classList.add("active");
        } else {
            star.classList.remove("active");
        }

        stars.push(star);
        container.appendChild(star);
    }

    return container;
}

// Ostrzeżenie "zmiany mogą nie zostać zapisane" przy zamykaniu/odświeżaniu strony, gdy
// jakieś śledzone pole (np. treść komentarza) ma niezapisaną zawartość. Sprawdzamy stan
// pól dopiero w momencie zamykania - dzięki temu nie trzeba ręcznie "wyrejestrowywać" pól
// usuniętych z DOM (np. po zamknięciu formularza) ani czyścić stanu po udanym wysłaniu.
const trackedUnsavedInputs = new Set<HTMLInputElement | HTMLTextAreaElement>();

export function trackUnsavedInput(el: HTMLInputElement | HTMLTextAreaElement): void {
    trackedUnsavedInputs.add(el);
}

function hasUnsavedInput(): boolean {
    for (const el of trackedUnsavedInputs) {
        if (document.body.contains(el) && el.value.trim().length > 0) {
            return true;
        }
    }
    return false;
}

window.addEventListener("beforeunload", (event) => {
    if (hasUnsavedInput()) {
        event.preventDefault();
        event.returnValue = "";
    }
});

// Ogranicza wysokość rozwijanej listy podpowiedzi do miejsca, jakie faktycznie zostało do dołu
// EKRANU (nie tylko do dołu popupu, który ją zawiera) - patrz .add-person-dialog w style.css
// (celowo overflow:visible, żeby lista mogła wystawać poza obrys popupu), gdzie bez tego
// ograniczenia mogłaby wystawać poza sam ekran i być nieosiągalna (brak scrolla strony pod
// otwartym <dialog>). Wołane tuż przed pokazaniem listy (patrz renderOptions/renderSuggestions w
// addLecturer.ts), bo pozycja pola na ekranie może się zmienić między otwarciami.
export function clampDropdownToViewport(input: HTMLElement, dropdown: HTMLElement): void {
    // 4px - margin-top samego dropdownu (patrz .search-suggestions w style.css) - dropdown
    // zaczyna się NIŻEJ niż dół pola, więc trzeba to doliczyć, inaczej zostawało kilka pikseli
    // ponad dołem ekranu. +16px to właściwy, widoczny odstęp od samej krawędzi.
    const margin = 4 + 16;
    const minHeight = 120; // ~3 pozycje - poniżej tego lista przestaje być użyteczna

    const inputRect = input.getBoundingClientRect();
    // getBoundingClientRect/innerHeight działają w przestrzeni WIZUALNEJ (po ewentualnym CSS
    // "zoom" na <body> - patrz zoom:110% w style.css dla myszy/desktopu), a max-height, które
    // ustawiamy niżej, jest we WŁASNEJ przestrzeni elementu (przed zoomem) - bez przeliczenia
    // dropdown renderowałby się o tyle procent za duży, ile wynosi zoom, i tak samo wychodził
    // poza dół ekranu (zgłoszenie: overshoot dokładnie o współczynnik zoomu).
    const zoomFactor = input.offsetHeight > 0 ? inputRect.height / input.offsetHeight : 1;
    const availableVisual = window.innerHeight - inputRect.bottom - margin;
    const available = availableVisual / zoomFactor;
    dropdown.style.maxHeight = `${Math.max(minHeight, Math.min(400, available))}px`;
}

// Obsługa klawiatury (strzałki + Enter) dla rozwijanych list podpowiedzi (.search-suggestions) -
// współdzielona przez wszystkie wyszukiwarki w serwisie (przedmiot, wydział, wykładowca, uczelnia).
export function attachDropdownKeyboardNav(input: HTMLInputElement, dropdown: HTMLElement): void {
    let activeIndex = -1;

    function getItems(): HTMLElement[] {
        return Array.from(dropdown.querySelectorAll<HTMLElement>(".search-suggestion-item"));
    }

    function setActive(index: number): void {
        const items = getItems();
        items.forEach(item => item.classList.remove("keyboard-active"));
        activeIndex = index;
        if (index >= 0 && index < items.length) {
            items[index].classList.add("keyboard-active");
            items[index].scrollIntoView({ block: "nearest" });
        }
    }

    input.addEventListener("keydown", (event) => {
        if (!dropdown.classList.contains("open")) return;
        const items = getItems();
        if (items.length === 0) return;

        if (event.key === "ArrowDown") {
            event.preventDefault();
            setActive(activeIndex < items.length - 1 ? activeIndex + 1 : 0);
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActive(activeIndex > 0 ? activeIndex - 1 : items.length - 1);
        } else if (event.key === "Enter") {
            // Zawsze blokujemy domyślne zachowanie (np. wysłanie formularza, w którym to
            // pole się znajduje) - Enter przy otwartej liście ma wybierać podpowiedź, nie
            // przypadkiem publikować cały formularz.
            event.preventDefault();
            const target = activeIndex >= 0 ? items[activeIndex] : items[0];
            target.click();
        } else if (event.key === "Escape") {
            dropdown.classList.remove("open");
            setActive(-1);
        }
    });

    // Nowe wpisywanie unieważnia poprzednie podświetlenie klawiaturą - lista i tak się
    // przebudowuje pod nowe zapytanie.
    input.addEventListener("input", () => setActive(-1));
}

// Ikona sortowania (dwie strzałki, w górę i w dół - standardowy symbol "sortuj") używana przez
// przycisk niżej. Sam SVG, bez etykiety - stąd wymagany aria-label na przycisku.
const SORT_ICON_SVG = `
<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
     stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M7 4v14"/><path d="M4 15l3 3 3-3"/>
    <path d="M17 20V6"/><path d="M20 9l-3-3-3 3"/>
</svg>`;

export interface SortMenuOption<T extends string> {
    value: T;
    label: string;
}

// Zamiennik zwykłego <select> na przycisk-ikonę + rozwijana lista opcji - te same dane, ten
// sam callback co przy "change" na select, ale opcje są schowane, dopóki użytkownik faktycznie
// nie kliknie ikony (mniejsze obciążenie poznawcze niż stały widoczny "Sortuj według: [...]").
// Używane przez browser.ts i post_detail.ts. toggleLabelText opcjonalny - post_detail.ts
// przekazuje "Sortuj" (widoczny napis obok strzałek, bo sama ikona ginęła użytkownikom na tej
// stronie), browser.ts go nie podaje i zostaje przy samej ikonie jak dotychczas.
export function renderSortMenu<T extends string>(
    options: SortMenuOption<T>[],
    initialValue: T,
    onChange: (value: T) => void,
    toggleLabelText?: string
): { element: HTMLElement, setValue: (value: T) => void } {
    let currentValue = initialValue;

    const wrapper = document.createElement("div");
    wrapper.className = "sort-menu";

    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = toggleLabelText ? "sort-menu-toggle sort-menu-toggle--labeled" : "sort-menu-toggle";
    toggle.innerHTML = toggleLabelText
        ? `<span class="sort-menu-toggle-label">${toggleLabelText}</span>${SORT_ICON_SVG}`
        : SORT_ICON_SVG;
    toggle.setAttribute("aria-haspopup", "true");
    toggle.setAttribute("aria-expanded", "false");

    const dropdown = document.createElement("div");
    dropdown.className = "sort-menu-dropdown";

    const optionButtons = options.map(option => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "sort-menu-option";
        btn.dataset.value = option.value;
        btn.textContent = option.label;
        btn.addEventListener("click", () => {
            currentValue = option.value;
            updateActiveOption();
            closeMenu();
            onChange(option.value);
        });
        dropdown.appendChild(btn);
        return btn;
    });

    function updateActiveOption(): void {
        const activeOption = options.find(o => o.value === currentValue);
        toggle.setAttribute("aria-label", `Sortuj według: ${activeOption?.label ?? ""} (zmień)`);
        optionButtons.forEach(btn => btn.classList.toggle("active", btn.dataset.value === currentValue));
    }

    let menuOpen = false;
    function closeMenu(): void {
        if (!menuOpen) return;
        menuOpen = false;
        dropdown.classList.remove("open");
        toggle.setAttribute("aria-expanded", "false");
        document.removeEventListener("click", handleOutsideClick);
    }
    function handleOutsideClick(event: MouseEvent): void {
        if (!wrapper.contains(event.target as Node)) {
            closeMenu();
        }
    }
    toggle.addEventListener("click", (event) => {
        event.stopPropagation();
        if (menuOpen) {
            closeMenu();
            return;
        }
        menuOpen = true;
        dropdown.classList.add("open");
        toggle.setAttribute("aria-expanded", "true");
        document.addEventListener("click", handleOutsideClick);
    });

    updateActiveOption();
    wrapper.append(toggle, dropdown);

    return {
        element: wrapper,
        setValue(value: T): void {
            currentValue = value;
            updateActiveOption();
        },
    };
}

// Wsparcie natywnych View Transitions między dokumentami (patrz @view-transition w
// browse.html/landing2.html/post_detail.html) jest w praktyce ograniczone do Chrome/Edge - gdzie
// indziej (Firefox, Safari) nawigujemy z ręcznym fade-outem (.page-fade-out, te same pliki), żeby
// przejście i tam nie wyglądało na twardy "przeskok" ekranu.
export function navigateSmoothly(url: string): void {
    // typeof (nie "in") - "in" zawężyłoby typ `document` w tej funkcji (nowsze @types/lib.dom
    // deklarują startViewTransition jako zawsze obecną metodę), co w przeglądarkach faktycznie
    // jej nieposiadających i tak zwróci undefined w runtime, ale poprawnie NIE zawęża typów.
    if (typeof document.startViewTransition === "function") {
        window.location.href = url;
        return;
    }
    document.body.classList.add("page-fade-out");
    window.setTimeout(() => {
        window.location.href = url;
    }, 200);
}
